import React, { useState, useEffect } from "react";
import api from "../utils/axiosConfig";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaEnvelope,
  FaLock,
  FaGoogle,
  FaTimes,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";


export default function Login({ onClose }) {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [visible, setVisible] = useState(true); // pour l'animation de fermeture
  const [showPassword, setShowPassword] = useState(false); // Pour afficher/masquer le mot de passe


  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";



  // Détecter le retour de l'authentification Google
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authStatus = urlParams.get('auth');
    const provider = urlParams.get('provider');
    const errorParam = urlParams.get('error');

    if (errorParam === 'google') {
      setError("Erreur lors de la connexion avec Google. Veuillez réessayer.");
    } else if (authStatus === 'success' && provider === 'google') {
      setSuccess("Connexion Google réussie !");
      setTimeout(() => {
        navigate(from, { replace: true });
        handleClose();
      }, 1500);
    }
  }, [navigate, from]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      await api.post('/api/auth/login', formData);

      setSuccess("Connexion réussie !");
      setFormData({ email: "", password: "" });

      setTimeout(() => {
        navigate(from, { replace: true });
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || "Erreur réseau ou serveur");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Redirection directe vers Google OAuth (fonctionne sur tous les navigateurs)
    const googleAuthUrl = `${process.env.REACT_APP_API_URL}/api/auth/google`;
    window.location.href = googleAuthUrl;
  };

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => {
      if (onClose) onClose(); // Ferme la modale dans le composant parent
      // Ne pas rediriger; on ferme juste la modale
    }, 300); // correspond au delay de framer-motion `exit`
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          style={styles.wrapper}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.3 }}
            style={styles.modal}
          >
            <button
              style={styles.closeBtn}
              onClick={handleClose}
              aria-label="Fermer la fenêtre de connexion"
            >
              <FaTimes />
            </button>

            <h2 style={styles.title}>Se connecter</h2>

            <form onSubmit={handleSubmit}>
              <div style={styles.inputWrapper}>
                <FaEnvelope style={styles.inputIcon} />
                <input
                  type="email"
                  name="email"
                  placeholder="E-mail *"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  style={styles.input}
                />
              </div>

              <div style={styles.inputWrapper}>
                <FaLock style={styles.inputIcon} />
                <div style={styles.passwordContainer}>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Mot de passe *"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    style={styles.passwordInput}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={styles.eyeButton}
                    aria-label={
                      showPassword
                        ? "Masquer le mot de passe"
                        : "Afficher le mot de passe"
                    }
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={styles.submitBtn}
              >
                {loading ? "Connexion..." : "Se connecter"}
              </motion.button>
            </form>

            {error && <p style={styles.error}>{error}</p>}
            {success && <p style={styles.success}>{success}</p>}

            <motion.div
              style={styles.footer}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <span>Nouveau client ? </span>
              <span
                style={styles.createLink}
                onClick={() => navigate("/register")}
              >
                Créez votre compte
              </span>
            </motion.div>

            <motion.button
              onClick={handleGoogleLogin}
              style={styles.googleBtn}
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <div style={styles.googleIcon}>
                <FaGoogle />
              </div>
              <span style={styles.googleText}>Continuer avec Google</span>
            </motion.button>


          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const styles = {
  wrapper: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    backdropFilter: "blur(10px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    fontFamily: "'Cormorant Garamond', serif",
  },
  modal: {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(20px)",
    padding: "50px 40px",
    borderRadius: "20px",
    width: "100%",
    maxWidth: "600px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
    border: "1px solid rgba(212, 175, 55, 0.3)",
    position: "relative",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  closeBtn: {
    position: "absolute",
    top: "15px",
    right: "15px",
    fontSize: "22px",
    color: "#2c1810",
    cursor: "pointer",
    background: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(212, 175, 55, 0.2)",
    borderRadius: "50%",
    padding: "8px",
    transition: "all 0.3s ease",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    fontSize: "28px",
    fontWeight: "600",
    marginBottom: "30px",
    color: "#2c1810",
    fontFamily: "'Cormorant Garamond', serif",
    letterSpacing: "1px",
  },
  inputWrapper: {
    position: "relative",
    marginBottom: "20px",
    width: "100%",
    maxWidth: "400px",
    display: "flex",
    alignItems: "center",
  },
  inputIcon: {
    position: "absolute",
    top: "50%",
    left: 15,
    transform: "translateY(-50%)",
    color: "#5a4a3a",
  },
  input: {
    width: "100%",
    padding: "8px 15px 8px 40px",
    borderRadius: "20px",
    border: "1px solid rgba(212, 175, 55, 0.2)",
    fontSize: "14px",
    transition: "all 0.3s ease",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    color: "#2c1810",
    fontFamily: "'Cormorant Garamond', serif",
  },
  passwordContainer: {
    position: "relative",
    width: "100%",
    maxWidth: "400px",
    display: "flex",
    alignItems: "center",
  },
  passwordInput: {
    width: "100%",
    padding: "8px 15px 8px 40px",
    paddingRight: "45px", // Espace pour le bouton œil
    borderRadius: "20px",
    border: "1px solid rgba(212, 175, 55, 0.2)",
    fontSize: "14px",
    transition: "all 0.3s ease",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    color: "#2c1810",
    fontFamily: "'Cormorant Garamond', serif",
  },
  eyeButton: {
    position: "absolute",
    right: "10px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(212, 175, 55, 0.2)",
    cursor: "pointer",
    padding: "6px",
    color: "#5a4a3a",
    fontSize: "14px",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "20px",
    width: "28px",
    height: "28px",
  },
  submitBtn: {
    width: "100%",
    maxWidth: "400px",
    padding: "8px 15px",
    border: "1px solid rgba(212, 175, 55, 0.2)",
    borderRadius: "20px",
    background: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    color: "#2c1810",
    cursor: "pointer",
    fontSize: "14px",
    fontFamily: "'Cormorant Garamond', serif",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    transition: "all 0.3s ease",
    marginTop: "20px",
  },
  footer: {
    marginTop: "30px",
    textAlign: "center",
    borderTop: "1px solid rgba(212, 175, 55, 0.2)",
    paddingTop: "20px",
    fontSize: "16px",
    fontFamily: "'Cormorant Garamond', serif",
    color: "#5a4a3a",
  },
  createLink: {
    color: "#d4af37",
    fontWeight: "600",
    cursor: "pointer",
    marginLeft: "5px",
    textDecoration: "underline",
    fontFamily: "'Cormorant Garamond', serif",
    transition: "all 0.3s ease",
  },
  googleBtn: {
    marginTop: "30px",
    width: "100%",
    maxWidth: "400px",
    padding: "8px 15px",
    border: "1px solid rgba(212, 175, 55, 0.2)",
    borderRadius: "20px",
    background: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    color: "#2c1810",
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "12px",
    transition: "all 0.3s ease",
    fontSize: "14px",
    position: "relative",
    overflow: "hidden",
    fontFamily: "'Cormorant Garamond', serif",
  },
  googleIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "20px",
    height: "20px",
    color: "#d4af37",
    fontSize: "18px",
  },
  googleText: {
    fontWeight: "600",
    color: "#2c1810",
    fontFamily: "'Cormorant Garamond', serif",
  },
  error: {
    color: "#d32f2f",
    marginTop: "15px",
    textAlign: "center",
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: "16px",
  },
  success: {
    color: "#4caf50",
    marginTop: "15px",
    textAlign: "center",
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: "16px",
  },
};